// Core & Router
import React, { Component } from 'react'; import { Link } from 'react-router-dom';

// HTTP
import axios from 'axios';

// JWT decode library
import { jwtDecode } from 'jwt-decode';

// Bootstrap
import { Row, Col, Breadcrumb } from 'react-bootstrap';

// RSuite UI
import { HStack, SelectPicker, Text, Panel, Loader } from 'rsuite';

// Charts
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Device detect
import MobileDetect from 'mobile-detect';

export default class DashboardPage extends Component {

    constructor(props) {
        super(props);
        this.state = {
            content: null,
            yearData: [],
            year: '',
            loading: false
        };

        // Initialize MobileDetect
        this.md = new MobileDetect(window.navigator.userAgent);

        ChartJS.register(
            ArcElement,
            CategoryScale,
            LinearScale,
            BarElement,
            PointElement,
            LineElement,
            Title,
            Tooltip,
            Legend
        );
    }
    async componentDidMount() {
        // Add resize listener only for mobile devices or desktop
        if (this.md.mobile()) {
            window.addEventListener('orientationchange', this.handleResize);
        } else {
            window.addEventListener('resize', this.handleResize);
        }

        const token = localStorage.getItem('token');

        // ตรวจสอบ token ก่อน
        if (!token) {
            window.location.href = '/admin/login';
            return;
        }

        try {
            // decode JWT
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000; // เป็นวินาที

            if (decoded.exp && decoded.exp < currentTime) {
                // Token หมดอายุ
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
                return;
            }

            // ถ้า token ยังใช้ได้ สร้าง authHeader และ set ลง state
            const authHeader = {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            };

            await this.promisedSetState({ authHeader });

            // เรียก API
            const response = await axios.get(`${process.env.REACT_APP_API}/status/1`, authHeader);
            const default_year = response.data[0].default_year;

            await this.promisedSetState({ year: default_year });

            // Render content
            await this.onRenderContent({ mode: 'read', year: default_year });

        } catch (error) {
            if (error.response && error.response.status === 401) {
                // Token หมดอายุหรือไม่ถูกต้อง
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                console.error('Error:', error);
            }
        }
    }

    // Debounced Resize handler
    handleResize = this.debounce(async () => {

        await this.promisedSetState({ content: null })

        const response = await axios.get(`${process.env.REACT_APP_API}/status/1`, this.state.authHeader);
        const default_year = response.data[0].default_year;

        await this.promisedSetState({ year: default_year })

        await this.onRenderContent({ mode: 'read', year: default_year });

    }, 300);

    // Debounce function to limit the frequency of resize handling
    debounce(func, delay) {
        let timeoutId;
        return (...args) => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(null, args);
            }, delay);
        };
    }

    // Promise-based setState
    promisedSetState = (newState) => new Promise(resolve =>
        this.setState(newState, resolve)
    );

    fetchYearData = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API}/years`, this.state.authHeader);

            // สร้างรายการปีที่ได้จาก API
            const formattedData = response.data.map(year => ({
                value: year.year_name,
                label: year.year_name
            }));

            // เพิ่ม option "ทั้งหมด" ไว้บนสุด
            const fullOption = { value: '', label: 'ทั้งหมด' };
            const yearDataWithAll = [fullOption, ...formattedData];

            await this.promisedSetState({ yearData: yearDataWithAll });

        } catch (err) {
            console.log({ error: 'Failed to load fetchYearData data' });
        }
    };

    onRenderContent = async ({ mode, year }) => {
        if (mode === 'read') {
            try {
                await this.promisedSetState({ loading: true })
                await this.fetchYearData();

                const config = this.state.authHeader; // สมมติว่าเป็น { headers: { Authorization: 'Bearer ...' } }

                // ดึงจำนวนผู้สมัครทั้งหมดและผู้ได้รับทุน
                const [appResponse, passedResponse] = await Promise.all([
                    axios.post(`${process.env.REACT_APP_API}/app`, { year }, config),
                    axios.post(`${process.env.REACT_APP_API}/passed`, { year }, config)
                ]);

                const applicants = appResponse.data.count;
                const studentsWithScholarships = passedResponse.data.count;
                const studentsWithoutScholarships = applicants - studentsWithScholarships;

                // ดึงข้อมูลตามระดับการศึกษา
                const levelResponse = await axios.post(`${process.env.REACT_APP_API}/level`, { year }, config);
                const data = levelResponse.data;

                const levelLabel = data.map(item => {
                    switch (item.edu_level) {
                        case 'm6': return 'ม.6';
                        case 'v3': return 'ปวช.3';
                        case 'h2': return 'ปวส.2';
                        default: return item.edu_level;
                    }
                });
                const levelValue = data.map(item => item.count);

                // ดึงข้อมูลหมู่บ้าน
                const villageResponse = await axios.post(`${process.env.REACT_APP_API}/village`, { year }, config);
                const villagesLabels = villageResponse.data.map(v => v.label);
                const villagesStudents = villageResponse.data.map(v => v.value);

                // ดึงข้อมูลโรงเรียน
                const schoolsResponse = await axios.post(`${process.env.REACT_APP_API}/school`, { year }, config);
                const sorted = schoolsResponse.data.sort((a, b) => b.value - a.value);
                const schoolsLabels = sorted.map(s => s.label);
                const schoolsStudents = sorted.map(s => s.value);

                // ดึงจำนวนกิจกรรมและผู้เข้าร่วม
                const [activityResponse, participantsResponse] = await Promise.all([
                    axios.post(`${process.env.REACT_APP_API}/student-activities/count`, { year }, config),
                    axios.post(`${process.env.REACT_APP_API}/student-participants/count`, { year }, config)
                ]);

                const activityCount = activityResponse.data.total;
                const participantCount = participantsResponse.data.total;

                // ดึงจำนวนการปรึกษาทั้งหมด
                const consultationResponse = await axios.post(`${process.env.REACT_APP_API}/student-consultation/count`, { year }, config);
                const consultationCount = consultationResponse.data.total;

                // ดึงข้อมูลการปรึกษาแยกรายเดือน
                const monthlyConsultationsResponse = await axios.post(`${process.env.REACT_APP_API}/student-consultation/monthly-count`, { year }, config);
                const monthlyConsultations = monthlyConsultationsResponse.data;

                const monthNames = [
                    'มกราคม',
                    'กุมภาพันธ์',
                    'มีนาคม',
                    'เมษายน',
                    'พฤษภาคม',
                    'มิถุนายน',
                    'กรกฎาคม',
                    'สิงหาคม',
                    'กันยายน',
                    'ตุลาคม',
                    'พฤศจิกายน',
                    'ธันวาคม'
                ];

                const monthlyLabels = monthNames;
                const monthlyValues = [];

                for (let m = 1; m <= 12; m++) {
                    const found = monthlyConsultations.find(item => item.month === m);
                    monthlyValues.push(found ? found.total : 0);
                }

                const topicCountResponse = await axios.post(
                    `${process.env.REACT_APP_API}/student-consultation/topic-count-by-year`,
                    { year },
                    config
                );

                const topicCountData = topicCountResponse.data;

                const topicLabels = topicCountData.map(item => item.label);
                const topicTotals = topicCountData.map(item => item.total);

                // Setting state using promisedSetState
                await this.promisedSetState({
                    content: <>
                        <Row className='mt-3'>
                            <Col>
                                <Breadcrumb size='lg' >
                                    <Breadcrumb.Item><Link to="/admin"><i className="bi bi-house-fill"></i></Link></Breadcrumb.Item>
                                    <Breadcrumb.Item active>Dashboard</Breadcrumb.Item>
                                </Breadcrumb>
                            </Col>
                        </Row>

                        <Row className='mb-3'>
                            <Col>
                                <HStack>
                                    <label><b>ปีการศึกษา</b></label>
                                    <SelectPicker size='lg'
                                        data={this.state.yearData}
                                        searchable={false}
                                        placeholder='ทั้งหมด'
                                        defaultValue={this.state.year}
                                        style={{ width: '200px' }}
                                        onChange={async e => {
                                            await this.promisedSetState({ year: e })
                                            this.onRenderContent({ mode: 'read', year: e });
                                        }}
                                        onClean={async () => {
                                            await this.promisedSetState({ year: '' })
                                            this.onRenderContent({ mode: 'read', year: '' });
                                        }}
                                    />
                                </HStack>
                            </Col>
                        </Row>

                        <Row>
                            <Col lg={3} className='mb-3'>
                                <Panel
                                    header="จำนวนผู้สมัคร"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em', height: '100%' }}
                                >
                                    <div className="text-center">
                                        <Text style={{ fontSize: '8rem' }}>{applicants.toLocaleString()}</Text> {/* Using appCount dynamically */}
                                    </div>
                                </Panel>
                            </Col>
                            <Col lg={3} className='mb-3'>
                                <Panel
                                    header="นักเรียนที่ได้รับทุนการศึกษา"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em', height: '100%' }}
                                >
                                    <div className="text-center">
                                        <Text style={{ fontSize: '8rem' }}>{studentsWithScholarships.toLocaleString()}</Text> {/* Dynamically displaying scholarship count */}
                                    </div>
                                </Panel>
                            </Col>
                            <Col lg={3} className='mb-3'>
                                <Panel
                                    header="สัดส่วนผู้สมัครและได้รับทุนการศึกษา"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em' }}
                                >
                                    <Pie
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (tooltipItem) => {
                                                            const total = tooltipItem.dataset.data.reduce((acc, curr) => acc + curr, 0); // Calculate total value
                                                            const percentage = ((tooltipItem.raw / total) * 100).toFixed(2); // Calculate percentage
                                                            return `${tooltipItem.label}: ${tooltipItem.raw} คน (${percentage}%)`; // Add percentage to tooltip
                                                        },
                                                    },
                                                },
                                                datalabels: {
                                                    color: 'white', // Label color
                                                    font: {
                                                        weight: 'bold', // Make the font bold
                                                    },
                                                    formatter: (value, context) => {
                                                        const total = context.chart._metasets[context.datasetIndex].data.reduce((acc, curr) => acc + curr, 0); // Calculate total
                                                        const percentage = ((value / total) * 100).toFixed(2); // Calculate percentage
                                                        return `${percentage}%`; // Return the percentage as a label
                                                    },
                                                    anchor: 'center', // Position the label in the center of the pie slices
                                                    align: 'center',  // Align the label at the center
                                                },
                                            },
                                            elements: {
                                                arc: {
                                                    borderWidth: 1,
                                                    borderColor: '#fff', // White border around slices
                                                },
                                            },
                                        }}
                                        data={{
                                            labels: ['ได้รับ', 'ไม่ได้รับ'], // Labels for the slices
                                            datasets: [
                                                {
                                                    label: 'จำนวนผู้สมัคร',
                                                    data: [studentsWithScholarships, studentsWithoutScholarships], // Data for the pie chart
                                                    backgroundColor: ['rgba(34, 193, 195, 1)', 'rgba(211, 211, 211, 1)'], // Colors for slices
                                                    hoverBackgroundColor: ['rgba(34, 193, 195, 0.8)', 'rgba(211, 211, 211, 0.8)'], // Hover colors
                                                },
                                            ],
                                        }}
                                    />
                                </Panel>
                            </Col>
                            <Col lg={3} className='mb-3'>
                                <Panel
                                    header="ระดับการศึกษาของผู้สมัคร"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em' }}
                                >
                                    <Pie
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (tooltipItem) => {
                                                            // Calculate the total value of the pie chart data
                                                            const total = tooltipItem.dataset.data.reduce((acc, curr) => acc + curr, 0);
                                                            // Calculate the percentage of the current slice
                                                            const percentage = ((tooltipItem.raw / total) * 100).toFixed(2);
                                                            // Format the tooltip label with value and percentage
                                                            return `${tooltipItem.label}: ${tooltipItem.raw} คน (${percentage}%)`;
                                                        },
                                                    },
                                                },
                                                datalabels: {
                                                    color: 'white', // Change the color of the data labels (optional)
                                                    font: {
                                                        weight: 'bold', // Make the font bold (optional)
                                                    },
                                                    formatter: (value, context) => {
                                                        const total = context.chart._metasets[context.datasetIndex].data.reduce((acc, curr) => acc + curr, 0);
                                                        const percentage = ((value / total) * 100).toFixed(2);
                                                        return `${percentage}%`; // Display percentage
                                                    },
                                                    anchor: 'end',
                                                    align: 'start',
                                                },
                                            },
                                        }}
                                        data={{
                                            labels: levelLabel, // Pie chart categories
                                            datasets: [
                                                {
                                                    label: 'จำนวนผู้สมัคร',
                                                    data: levelValue, // Data points (number of students)
                                                    backgroundColor: [
                                                        'rgba(34, 193, 195, 1)',  // Light Teal
                                                        'rgba(253, 187, 45, 1)',  // Golden Yellow
                                                        'rgba(255, 94, 77, 1)',   // Coral Red
                                                    ],
                                                    hoverBackgroundColor: [
                                                        'rgba(34, 193, 195, 0.8)',  // Light Teal (hover)
                                                        'rgba(253, 187, 45, 0.8)',  // Golden Yellow (hover)
                                                        'rgba(255, 94, 77, 0.8)',   // Coral Red (hover)
                                                    ],
                                                },
                                            ],
                                        }}
                                    />
                                </Panel>
                            </Col>
                        </Row>

                        <Row>
                            <Col className='mb-3'>
                                <Panel
                                    header="หมู่บ้านที่มีนักเรียนได้รับทุนการศึกษา"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em' }}
                                >
                                    <Bar
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                },
                                            },
                                        }}
                                        data={{
                                            labels: villagesLabels, // Labels (School names)
                                            datasets: [
                                                {
                                                    type: 'bar',
                                                    label: 'นักเรียนที่ได้รับทุน', // Label for the bar
                                                    data: villagesStudents, // Data points (number of students for each school)
                                                    backgroundColor: Array(villagesStudents.length).fill('rgba(30,144,255, 1)'), // Set color for each bar
                                                },
                                            ],
                                        }}
                                    />
                                </Panel>
                            </Col>
                        </Row>

                        <Row>
                            <Col className='mb-3'>
                                <Panel
                                    header="10 สถานศึกษาที่เข้าเรียนต่อมากที่สุด"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em' }}
                                >
                                    <Bar
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                },
                                            },
                                        }}
                                        data={{
                                            labels: schoolsLabels, // Labels (School names)
                                            datasets: [
                                                {
                                                    type: 'bar',
                                                    label: 'นักเรียนที่ได้รับทุน', // Label for the bar
                                                    data: schoolsStudents, // Data points (number of students for each school)
                                                    backgroundColor: Array(schoolsLabels.length).fill('rgba(30,144,255, 1)'), // Set color for each bar
                                                },
                                            ],
                                        }}
                                    />
                                </Panel>
                            </Col>
                        </Row>

                        <Row>
                            <Col lg={3} className='mb-3'>
                                <Panel
                                    header="จำนวนกิจกรรมของนักเรียนหรือเยาวชน"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em', height: '100%' }}
                                >
                                    <div className="text-center">
                                        <Text style={{ fontSize: '8rem' }}>{activityCount.toLocaleString()}</Text> {/* Using appCount dynamically */}
                                    </div>
                                </Panel>
                            </Col>
                            <Col lg={3} className='mb-3'>
                                <Panel
                                    header="จำนวนผู้เข้าร่วมกิจกรรมฯ"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em', height: '100%' }}
                                >
                                    <div className="text-center">
                                        <Text style={{ fontSize: '8rem' }}>{participantCount.toLocaleString()}</Text> {/* Dynamically displaying scholarship count */}
                                    </div>
                                </Panel>
                            </Col>
                            <Col lg={3} className='mb-3'>
                                <Panel
                                    header="จำนวนให้คำปรึกษา (ตามช่วงปี พ.ศ.)"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em', height: '100%' }}
                                >
                                    <div className="text-center">
                                        <Text style={{ fontSize: '8rem' }}>{consultationCount.toLocaleString()}</Text> {/* Using appCount dynamically */}
                                    </div>
                                </Panel>
                            </Col>
                            <Col lg={3} className='mb-3'>
                                <Panel
                                    header="จำนวนการให้คำปรึกษาในแต่ละเดือน (ตามช่วงปี พ.ศ.)"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em' }}
                                >
                                    <Pie
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: {
                                                    display: false  // เอา legend ออก
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (tooltipItem) => {
                                                            const total = tooltipItem.dataset.data.reduce((acc, curr) => acc + curr, 0);
                                                            const percentage = ((tooltipItem.raw / total) * 100).toFixed(2);
                                                            return `${tooltipItem.label}: ${tooltipItem.raw} คน (${percentage}%)`;
                                                        },
                                                    },
                                                },
                                                datalabels: {
                                                    color: 'white',
                                                    font: { weight: 'bold' },
                                                    formatter: (value, context) => {
                                                        const total = context.chart._metasets[context.datasetIndex].data.reduce((acc, curr) => acc + curr, 0);
                                                        const percentage = ((value / total) * 100).toFixed(2);
                                                        return `${percentage}%`;
                                                    },
                                                    anchor: 'end',
                                                    align: 'start',
                                                },
                                            },
                                        }}
                                        data={{
                                            labels: monthlyLabels,
                                            datasets: [{
                                                label: 'จำนวนการปรึกษา',
                                                data: monthlyValues,
                                                backgroundColor: [
                                                    '#22C1C3', '#FDBB2D', '#FF5E4D', '#6a1b9a', '#00897b', '#f4511e',
                                                    '#3949ab', '#c0ca33', '#00acc1', '#ff7043', '#546e7a', '#d81b60',
                                                ],
                                                hoverBackgroundColor: [
                                                    'rgba(34, 193, 195, 0.8)', 'rgba(253, 187, 45, 0.8)', 'rgba(255, 94, 77, 0.8)',
                                                    'rgba(106, 27, 154, 0.8)', 'rgba(0, 137, 123, 0.8)', 'rgba(244, 81, 30, 0.8)',
                                                    'rgba(57, 73, 171, 0.8)', 'rgba(192, 202, 51, 0.8)', 'rgba(0, 172, 193, 0.8)',
                                                    'rgba(255, 112, 67, 0.8)', 'rgba(84, 110, 122, 0.8)', 'rgba(216, 27, 96, 0.8)',
                                                ],
                                            }],
                                        }}
                                    />
                                </Panel>
                            </Col>
                        </Row>

                        <Row>
                            <Col className='mb-3'>
                                <Panel
                                    header="แผนภูมิแท่งแสดงประเด็นหรือปัญหาที่นำมาปรึกษา (ตามช่วงปี พ.ศ.)"
                                    className="shadow-sm"
                                    style={{ backgroundColor: '#FFF', padding: '0.5em' }}
                                >
                                    <Bar
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                },
                                            },
                                        }}
                                        data={{
                                            labels: topicLabels,
                                            datasets: [
                                                {
                                                    label: 'จำนวนครั้งที่ปรึกษา',
                                                    data: topicTotals,
                                                    backgroundColor: 'rgba(30,144,255, 1)',
                                                },
                                            ],
                                        }}
                                    />
                                </Panel>
                            </Col>
                        </Row>
                    </>
                });

                await this.promisedSetState({ loading: false })

            } catch (error) {
                // Handle errors here
                console.error('Error fetching data or rendering content:', error);

                // Redirect to Log In
                window.location.href = '/admin/login';
            }
        }
    };


    render() {

        const FullPageLoader = () => (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.8)', // Semi-transparent background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999 // Ensure it's on top of other content
            }}>
                <Loader content="Loading..." /> {/* Adjust loader component as needed */}
            </div>
        );

        return (
            <>
                {this.state.content}
                {this.state.loading && <FullPageLoader />}
            </>
        );
    }
}
